import { useRef, useState, useCallback } from 'react';
import { useTranslation } from './useTranslations.js';
import { catchFatalError } from '../utils/utils.js';
import { DEFAULT_ADYEN_STATE, DEFAULT_ERRORS, getFromAdyenCardBrand, isAdyenStateValid, convertAdyenState, addRequiredMessageIfNeeded, maybeErrorForHolderName, getLastFourDigits, parseStateOnChange, areIdenticalCardErrors, getFocusedElement } from './useAdyenState.static.js';

/**
 * Because we use the 'securedfields' of AdyenCheckout, we need to pass several callbacks to AdyenCheckout in order to gather the encrypted information.
 *
 * This hook is providing these necessary callbacks and collect everything into a ref.
 *
 * In addition, it also collects the holderName and the storeInVault data, as the associated inputs are not covered by the 'securedfields' approach.
 *
 * For validation and retrieval of the encrypted data, this hook provides the function 'computeAdyenCreditCardFormData'.
 */
const useAdyenState = (onFatalError) => {
    const adyenStateRef = useRef({
        ...DEFAULT_ADYEN_STATE,
        paymentMethod: { ...DEFAULT_ADYEN_STATE.paymentMethod },
    });
    const [storeInVault, setStoreInVault] = useState(DEFAULT_ADYEN_STATE.storeInVault);
    const [creditCardBrand, setCreditCardBrand] = useState(DEFAULT_ADYEN_STATE.creditCardBrand);
    // The detected and supported card brands from the BIN lookup operation.
    const [detectedCardBrands, setDetectedCardBrands] = useState([]);
    const [focusedElement, setFocusedElement] = useState(null);
    const [errors, setErrors] = useState({ ...DEFAULT_ERRORS });
    const { t } = useTranslation();
    const requiredMessage = t('checkout.form.required.error');
    // The holderName is not managed by Adyen so we keep track of the changes ourselves.
    const onHolderNameChange = useCallback(catchFatalError(onFatalError, (event) => {
        const holderName = event.target.value;
        adyenStateRef.current.paymentMethod.holderName = holderName;
        setErrors((errors) => {
            const errorForHolderName = maybeErrorForHolderName(holderName, t);
            // if no change in errors, return the given 'errors' to tell React not to re-render
            return errors.holderName === errorForHolderName
                ? errors
                : { ...errors, holderName: errorForHolderName };
        });
    }), [adyenStateRef, onFatalError]);
    // We use the Adyen 'onFieldValid' callback only to get the last four digits of the card number.
    // The actual validity of fields is managed with 'onChange' below.
    const onFieldValid = useCallback(catchFatalError(onFatalError, (data) => {
        const nullableLastFour = getLastFourDigits(data);
        // as 'onFieldValid' is called for every input but only the 'encryptedCardNumber' has the last four digits, we update the state only when needed
        if (nullableLastFour !== null) {
            adyenStateRef.current.lastFour = nullableLastFour;
        }
    }), [adyenStateRef, onFatalError]);
    // 'storeInVault' is also tracked with a hook to re-render the checkbox when storeInVault has changed
    const onStoreInVaultChange = useCallback(catchFatalError(onFatalError, (event) => {
        const storeInVault = event.target.checked;
        adyenStateRef.current.storeInVault = storeInVault;
        setStoreInVault(storeInVault);
    }), [adyenStateRef, onFatalError]);
    // Adyen has the specific callback 'onBrand' for when it detects the brand of the card.
    // And 'brand' is also tracked with a hook to re-render the card icons and adapt the UI to Amex cards.
    const onBrand = useCallback(catchFatalError(onFatalError, (brandData) => {
        const creditCardBrand = getFromAdyenCardBrand(brandData);
        adyenStateRef.current.creditCardBrand = creditCardBrand;
        setCreditCardBrand(creditCardBrand);
    }), [adyenStateRef, onFatalError]);
    /* The callback to be used to perform Adyen secured fields "onBinLookup", the event will be fired
     * when the users complete input the card BIN (first 6 to 8 digits). */
    const onBinLookup = useCallback((binLookupResult) => {
        const cardBrands = binLookupResult.supportedBrands?.map((brand) => getFromAdyenCardBrand(brand));
        setDetectedCardBrands(cardBrands || []);
    }, []);
    // Adyen provides the 'onChange' callback that is triggered when the customer entered his card information.
    // It contains the bulk of the data we need, as well as the validation errors of the fields, if any.
    const onChange = useCallback(catchFatalError(onFatalError, (state) => {
        const parsedState = parseStateOnChange(state);
        adyenStateRef.current.isValid = parsedState.isValid;
        Object.assign(adyenStateRef.current.paymentMethod, parsedState.paymentMethod);
        setErrors((errors) => {
            // if no change in errors, return the given 'errors' to tell React not to re-render
            return areIdenticalCardErrors(errors, parsedState.errors)
                ? errors
                : Object.assign(parsedState.errors, {
                    holderName: errors.holderName,
                });
        });
    }), [adyenStateRef, onFatalError]);
    const onFocus = useCallback(catchFatalError(onFatalError, (data) => {
        setFocusedElement(getFocusedElement(data));
    }), [adyenStateRef, onFatalError]);
    // Return the state if all is fine: no errors on any field & all the encrypted data is present
    const computeAdyenCreditCardFormData = useCallback(() => {
        try {
            const state = adyenStateRef.current;
            if (isAdyenStateValid(state)) {
                return convertAdyenState(state);
            }
            else {
                setErrors((errors) => {
                    const newErrors = addRequiredMessageIfNeeded(errors, state, requiredMessage);
                    // no need to return a new 'errors' object if there's nothing to change
                    return Object.keys(newErrors).length === 0 ? errors : { ...errors, ...newErrors };
                });
                return null;
            }
        }
        catch (error) {
            onFatalError(error);
            return null;
        }
    }, [adyenStateRef, requiredMessage, onFatalError]);
    return {
        adyenStateRef,
        detectedCardBrands,
        onChange,
        onFieldValid,
        onHolderNameChange,
        storeInVault,
        onStoreInVaultChange,
        focusedElement,
        onFocus,
        creditCardBrand,
        onBrand,
        onBinLookup,
        computeAdyenCreditCardFormData,
        errors,
    };
};

export { useAdyenState as default };
//# sourceMappingURL=useAdyenState.js.map
